import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { AttachFile as AttachIcon, FolderOpen as FolderIcon } from '@mui/icons-material';
import { enhancedApi } from '../../api';

const MAX_BYTES = 25 * 1024 * 1024;

export default function AttachmentPicker({ open, onClose, clientId, onConfirm }) {
  const [tab, setTab] = useState(0);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [uploadQueue, setUploadQueue] = useState([]);

  const reset = useCallback(() => {
    setTab(0);
    setFiles([]);
    setError(null);
    setSelectedIds(new Set());
    setUploadQueue([]);
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (!clientId || tab !== 1) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await enhancedApi.getClientFiles(clientId, false);
        const payload = res?.data || res;
        const rawFiles = payload?.files || [];
        const mapped = rawFiles.map((f) => ({
          existing_document_id: String(f.id),
          filename: f.file_name || 'file',
          size_bytes: f.file_size || 0,
          folder_id: f.folder_id,
        }));
        if (!cancelled) setFiles(mapped);
      } catch {
        if (!cancelled) {
          setError('Could not load client files.');
          setFiles([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, clientId, tab, reset]);

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleFileInput = (e) => {
    const picked = Array.from(e.target.files || []);
    const next = [];
    picked.forEach((file) => {
      if (file.size > MAX_BYTES) {
        setError(`"${file.name}" exceeds 25 MB.`);
        return;
      }
      next.push({
        file,
        filename: file.name,
        size_bytes: file.size,
      });
    });
    setUploadQueue((q) => [...q, ...next]);
    e.target.value = '';
  };

  const removeQueued = (idx) => {
    setUploadQueue((q) => q.filter((_, i) => i !== idx));
  };

  const handleConfirm = () => {
    const fromExisting = files.filter((f) => selectedIds.has(f.existing_document_id));
    const merged = [...uploadQueue, ...fromExisting];
    onConfirm(merged);
    onClose();
  };

  const disableClientFiles = !clientId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Attachments</DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Upload new" />
          <Tab label="From client files" disabled={disableClientFiles} />
        </Tabs>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {tab === 0 && (
          <Box>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: 'background.default',
              }}
              component="label"
            >
              <input type="file" hidden multiple onChange={handleFileInput} />
              <AttachIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Drop files here or click to browse (max 25 MB each)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
              {uploadQueue.map((item, idx) => (
                <Chip
                  key={`${item.filename}-${idx}`}
                  label={`${item.filename} (${Math.round(item.size_bytes / 1024)} KB)`}
                  onDelete={() => removeQueued(idx)}
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )}
        {tab === 1 && (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : files.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <FolderIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No files at client root. Upload to the client Files tab first.
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {files.map((f) => (
                  <ListItem key={f.existing_document_id} disablePadding>
                    <ListItemButton onClick={() => toggleId(f.existing_document_id)} dense>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedIds.has(f.existing_document_id)}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText primary={f.filename} secondary={`${Math.round((f.size_bytes || 0) / 1024)} KB`} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm}>
          Add attachments
        </Button>
      </DialogActions>
    </Dialog>
  );
}
